import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import areaCalculation from 'js/algorithms/geometry/AreaCalculation';
import magic from 'js/components/PolygonDrawing/MagicSelection';
import debounce from 'lodash.debounce';
import { voidFunc } from 'js/constants/PropTypeUtils';
import ConfirmationDialog from '../EditFieldView/ConfirmationDialog/ConfirmationDialog';
import SplitFieldInputDialog from '../EditFieldView/SplitField/SplitFieldInputDialog';
import { validateDrawing } from './DrawingPopoverStatus/utils/DrawingValidation';
import {
  buildEditFieldConfirmation,
  buildMergeFieldConfirmation,
  buildSplitFieldConfirmation,
} from '../EditFieldView/ConfirmationDialog/ConfirmationPropsFactory';
import { DRAWING_MODES, PolygonDrawingContext } from './PolygonDrawingContext';
import {
  shouldMarkerSnapToFinish,
  convertToArrayOfLatLngRealNumbers,
  initializeDrawingState,
  getLat,
  getLng,
} from './PolygonDrawingUtils';
import MeasurementLines from './Components/MeasurementLines';
import PolygonMarkers from './Components/PolygonMarkers';
import DrawnPolygons from './Components/DrawnPolygons';
import DrawingPopover from './Components/DrawingPopover';
import SelectedMeasurementLine from './Components/SelectedMeasurementLine';
import SelectedMarker from './Components/SelectedMarker';
import PolygonToolbar from './PolygonToolbar';

export default class PolygonDrawing extends Component {
  static contextType = PolygonDrawingContext;
  constructor(props) {
    super(props);
    this.map = this.props.map;
    this.markerListener = null;
    this.polyline = null;
    this.initialized = false;
    this.noToolActiveListener = null;
  }

  // ------------------------Initialization & Setup------------------------ //
  setDrawingStateAndStartDrawing() {
    const { dispatch } = this.context;
    initializeDrawingState(
      dispatch,
      this.startDrawing,
      this.initialized,
      this.initializeEditFieldBoundaries,
      this.initializeEditMergedField,
      this.props
    );
  }

  initializeEditFieldBoundaries = () => {
    const { dispatch } = this.context;
    this.initialized = true;

    const path = this.props.selectedField.polygon.coordinates[0].map(([lng, lat]) => ({
      lat,
      lng,
    }));
    path.forEach(({ lat, lng }) => {
      const newMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        map: null,
        gmpClickable: true,
      });
    });

    const trimmedPath = path.slice(0, -1);
    const initialAction = [{ path: trimmedPath, polygons: [{ path: trimmedPath }] }];

    setTimeout(
      () =>
        dispatch({
          type: 'INITIALIZE_EDIT_FIELD_BOUNDARIES',
          payload: {
            actions: [initialAction],
            acIndex: 0,
            polygons: [{ path: trimmedPath, valid: true }],
          },
        }),
      0
    );
  };

  initializeEditMergedField = () => {
    const { dispatch } = this.context;
    this.initialized = true;

    const path = this.props.mergedFields.newCoordinates.map(([lng, lat]) => ({ lat, lng }));
    path.forEach(({ lat, lng }) => {
      const newMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        map: null,
        gmpClickable: true,
      });
    });

    const trimmedPath = path.slice(0, -1);
    const initialAction = [{ path: trimmedPath, polygons: [{ path: trimmedPath }] }];

    setTimeout(
      () =>
        dispatch({
          type: 'INITIALIZE_EDIT_MERGED_FIELD',
          payload: {
            actions: [initialAction],
            acIndex: 0,
            polygons: [{ path: trimmedPath, valid: true }],
          },
        }),
      0
    );
  };

  componentDidMount() {
    this.setDrawingStateAndStartDrawing();
  }

  componentDidUpdate(prev) {
    const doMagic = prev.magicWand.targetLatLng !== this.props.magicWand.targetLatLng;

    if (doMagic) {
      this.drawMagicSelection(this.props.magicWand);
    } else if (prev.drawnPolygon !== this.props.drawnPolygon) {
      const polygon = [{ valid: true, path: this.props.drawnPolygon }];

      const actions = [];
      const action = [...polygon];

      actions.push(action);
    }
  }

  // ------------------------Magic Selection------------------------ //
  drawMagicSelection = async (magicWand) => {
    const { fieldImage, surveys, selectedLayer, showSoilSurveys, selectedField } = this.props;
    const { actions, acIndex } = this.context.state;
    const { dispatch } = this.context;

    let newActions = [...actions];
    let imageUrl = fieldImage.url;

    if (showSoilSurveys) {
      const survey = surveys.find((survey) => survey.fieldId === selectedField.fieldId);
      imageUrl = survey.images[selectedLayer];
    }

    const { targetLatLng, field, isSelecting, threshold } = magicWand;

    if (!field || !targetLatLng || !isSelecting) {
      return null;
    }

    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(field.bounds.south, field.bounds.west),
      new google.maps.LatLng(field.bounds.north, field.bounds.east)
    );

    try {
      const selection = await magic(bounds, field.polygon, imageUrl, threshold, targetLatLng);
      const polygon = [{ valid: true, path: selection }];

      const action = [...polygon];

      if (acIndex < newActions.length - 1) {
        newActions = [];
      }

      newActions.push(action);
      dispatch({
        type: 'MAGIC_SELECTION',
        payload: {
          polygons: action,
          actions: newActions,
          acIndex: newActions.length - 1,
        },
      });
    } catch (e) {
      return null;
    }
  };

  handleThresholdCommitted = debounce((value) => {
    this.props.onSetMagicWandThreshold(value);
  }, 250);

  handleThresholdChanged = (event, value) => {
    const { dispatch } = this.context;
    dispatch({ type: 'SET_CHANGING_THRESHOLD', payload: value });
    this.setState({ changingThreshold: value });
    this.handleThresholdCommitted(value);
  };

  // ------------------------Start, Stop, Reset & Submit------------------------ //
  startDrawing = (drawingMode) => {
    // Init map
    this.map = this.props.map;
    if (drawingMode === DRAWING_MODES.SELECT) {
    } else {
      /* add listener for adding markers on map */
      this.map.setOptions({ draggableCursor: 'crosshair' });
      this.markerListener = google.maps.event.addListener(this.map, 'click', (event) => {
        const latLng = event.latLng;
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: latLng,
          map: this.map,
        });
        this.markerCompleteCallback(marker);
      });
    }
    this.context.dispatch({ type: 'SET_MEASURING', payload: false });
  };

  stopDrawing = () => {
    this.map.setOptions({ draggableCursor: null });
    this.resetHandler(true);
    const contextState = this.context.state;
    // Dispatch
    this.props.setIsMagicSelecting(false);
    this.props.setIsDrawingNote(false);
    this.props.setIsDrawingField(false);
    this.props.setIsPlacingMarker(false);
    this.props.setIsDrawingFieldHole(false);
    this.props.setIsEditingFieldBoundaries(false);
    this.props.setIsSplittingField(false);
    this.props.setIsEditingMergedField(false);
    if (
      contextState.drawingMode === 'EDIT_FIELD_BOUNDARIES' ||
      contextState.drawingMode === 'SPLIT_FIELD' ||
      contextState.drawingMode === 'SPLIT_FIELD_POLYGONS' ||
      contextState.drawingMode === 'EDIT_MERGED_FIELD'
    ) {
      this.props.returnToEditFieldView();
    }
  };

  resetHandler = (unmounting) => {
    // Remove the line from the map
    if (this.polyline) {
      this.polyline.setMap(null);
    }
    const { dispatch } = this.context;

    // Reset all variables for map
    dispatch({ type: 'RESET' });
    // Remove listeners
    google.maps.event.removeListener(this.markerListener);
    google.maps.event.removeListener(this.noToolActiveListener);

    if (unmounting) return;

    // Restart
    this.initialized = false;
    setTimeout(() => {
      this.setDrawingStateAndStartDrawing();
    }, 0);
  };

  shouldSubmitButtonDisable = () => {
    const { dispatch } = this.context;
    const { drawingMode, polygonVisible, polygons, markers, splitFieldInputs, mergedFieldInputs } =
      this.context.state;
    const { selectedField, LangFile } = this.props;

    const size = polygons
      .reduce((acc, polygon) => {
        const { size } = areaCalculation({ coords: polygon.path });
        return (acc += size);
      }, 0)
      .toFixed(2);

    const { result, updatedState } = validateDrawing({
      drawingMode,
      polygonVisible,
      polygons,
      markers,
      size,
      splitFieldInputs,
      mergedFieldInputs,
      selectedField,
      LangFile,
    });

    if (updatedState && updatedState.polygons && !updatedState.actions) {
      dispatch({
        type: 'SET_POLYGONS',
        payload: {
          polygons: updatedState.polygons,
          polygonVisible: updatedState.polygons.length > 0,
        },
      });
    } else if (updatedState) {
      dispatch({
        type: 'SET_SPLIT_FIELD_POLYGONS',
        payload: {
          polygons: updatedState.polygons,
          actions: updatedState.actions,
          acIndex: updatedState.acIndex,
        },
      });
    }

    return result;
  };

  onSubmit = () => {
    const { dispatch } = this.context;
    const { drawingMode, polygons } = this.context.state;

    if (polygons[0]) {
      const outline = polygons.map((p) => convertToArrayOfLatLngRealNumbers(p.path));
      switch (drawingMode) {
        case 'NOTE':
        case 'SELECT':
          for (const o of outline) {
            this.props.selectAndCreateNote(o);
          }
          this.stopDrawing();
          break;
        case 'FIELD':
          this.props.addDrawnField(outline[0]);
          this.stopDrawing();
          break;
        case 'FIELD_HOLE':
          this.props.addDrawnFieldHole(outline[0]);
          this.stopDrawing();
          break;
        case 'EDIT_FIELD_BOUNDARIES':
          dispatch({
            type: 'SET_SHOW_DIALOG',
            payload: {
              key: 'showEditFieldBoundariesConfirmation',
              value: true,
            },
          });
          break;
        case 'SPLIT_FIELD_POLYGONS':
          dispatch({
            type: 'SET_SHOW_DIALOG',
            payload: {
              key: 'showSplitFieldConfirmation',
              value: true,
            },
          });
          break;
        case 'EDIT_MERGED_FIELD':
          dispatch({
            type: 'SET_SHOW_DIALOG',
            payload: {
              key: 'showEditMergedFieldConfirmation',
              value: true,
            },
          });
          break;
        default:
          break;
      }
    } else if (drawingMode === 'SPLIT_FIELD') {
      dispatch({
        type: 'SET_SHOW_DIALOG',
        payload: {
          key: 'showSplitFieldInputDialog',
          value: true,
        },
      });
    }
  };

  // ------------------------Listeners------------------------ //
  toggleDraw = () => {
    const { measuring } = this.context.state;
    const { dispatch } = this.context;
    if (measuring) {
      this.removeMarkerListener();
      this.addMarkerListener();
    } else if (this.markerListener?.instance == null) {
      this.addMarkerListener();
    } else {
      this.removeMarkerListener();
    }
    dispatch({ type: 'SET_MEASURING', payload: false });
  };

  addMarkerListener = () => {
    google.maps.event.removeListener(this.noToolActiveListener);
    this.map.setOptions({ draggableCursor: 'crosshair' });
    this.markerListener = google.maps.event.addListener(this.map, 'click', (event) => {
      const latLng = event.latLng;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: latLng,
        map: this.map,
      });
      this.markerCompleteCallback(marker);
    });

    this.removeNoToolListener();
  };

  removeMarkerListener = () => {
    this.map.setOptions({ draggableCursor: null });
    google.maps.event.removeListener(this.markerListener);
    this.addNoToolListener();
  };

  addNoToolListener = () => {
    const { dispatch } = this.context;
    this.noToolActiveListener = google.maps.event.addListener(this.map, 'click', (event) => {
      this.props.onNoToolSelected();
      dispatch({ type: 'SET_PLAY_ANIMATION', payload: true });
      setTimeout(() => {
        dispatch({ type: 'SET_PLAY_ANIMATION', payload: false });
      }, 3000);
    });
  };

  removeNoToolListener = () => {
    google.maps.event.removeListener(this.noToolActiveListener);
  };

  // ------------------------Measurement & Polygon drawing/edit------------------------ //
  markerCompleteCallback = (marker) => {
    const { dispatch } = this.context;
    const contextState = this.context.state;
    let { markers, polygons, actions, acIndex, measurementMarkers, measuring, drawingMode } =
      contextState;

    // --- Handle measuring marker ---
    if (measuring) {
      if (marker) marker.setMap(null);
      const newMarker = { position: marker.position };
      const updatedMeasurementMarkers = [...measurementMarkers, newMarker];

      // Toggle measuring mode if even count (every 2nd marker)
      if (updatedMeasurementMarkers.length % 2 === 0) {
        this.measureHandler();
      }

      if (acIndex !== -1) actions = actions.slice(0, acIndex + 1);

      const newAction = {
        path: [...markers],
        measurementMarkers: [...updatedMeasurementMarkers],
        polygons: [...polygons],
      };

      const newActions = [...actions, [newAction]];
      const newAcIndex = newActions.length - 1;

      dispatch({
        type: 'ADD_MEASUREMENT_MARKER',
        payload: {
          measurementMarkers: updatedMeasurementMarkers,
          actions: newActions,
          acIndex: newAcIndex,
        },
      });

      return;
    }

    // --- Handle drawing marker ---
    if (drawingMode === 'MARKER') {
      this.props.onMarkerPlaced(marker);
      this.stopDrawing();
      return;
    }

    // Remove the temporary Google Maps marker — we'll redraw our own
    if (marker) marker.setMap(null);

    let finished = false;
    if (markers.length > 2) {
      if (marker && shouldMarkerSnapToFinish(this.context, this.map, marker)) {
        finished = true;
      }
    }

    if (finished) {
      this.polyline?.setMap(null);
      this.removeMarkerListener();

      const newPath = markers.map((m) => m.position);
      const newPolygon = { path: newPath };
      polygons = [newPolygon];

      // Trim redo history
      actions = acIndex !== -1 ? actions.slice(0, acIndex + 1) : [...actions];

      // Snapshot both layers
      const newAction = {
        path: [...markers.map((m) => ({ ...m }))],
        measurementMarkers: [...measurementMarkers],
        polygons: [...polygons],
      };

      const newActions = [...actions, [newAction]];
      const newAcIndex = newActions.length - 1;

      dispatch({
        type: 'FINISH_POLYGON',
        payload: {
          polygons,
          polygonVisible: true,
          actions: newActions,
          acIndex: newAcIndex,
        },
      });
    } else {
      const newMarker = { position: marker.position };
      const newMarkers = [...markers, newMarker];
      const trimmedActions = acIndex !== -1 ? actions.slice(0, acIndex + 1) : [...actions];
      const newAction = {
        path: [...newMarkers.map((m) => ({ ...m }))],
        measurementMarkers: [...measurementMarkers],
        polygons: [...polygons],
      };

      const newActions = [...trimmedActions, [newAction]];

      dispatch({
        type: 'ADD_MARKER',
        payload: {
          markers: newMarkers,
          actions: newActions,
          acIndex: newActions.length - 1,
        },
      });

      this.drawPolyline();
    }
  };

  measureHandler = () => {
    const { dispatch } = this.context;
    const { measuring } = this.context.state;
    if (this.markerListener?.instance !== null) this.removeMarkerListener();
    if (!measuring) this.addMarkerListener();
    dispatch({ type: 'SET_MEASURING', payload: !measuring });
  };

  drawPolyline = (markers) => {
    if (!markers) markers = this.context.state.markers;
    if (this.polyline != null) {
      this.polyline.setMap(null);
    }

    if (markers) {
      this.polyline = new google.maps.Polyline({
        path: markers
          .filter((p) => p.position)
          .map((poly) => new google.maps.LatLng(getLat(poly.position), getLng(poly.position))),
        fillColor: 'white',
        fillOpacity: 0.2,
        strokeColor: 'white',
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: this.map,
        clickable: false,
      });
    }
  };

  updateMeasurementMarkers = (index, latLng1, latLng2) => {
    const { dispatch } = this.context;
    const { measurementMarkers, markers, polygons } = this.context.state;

    const updatedMarkers = [...measurementMarkers];
    updatedMarkers[index] = { position: latLng1 };
    updatedMarkers[index + 1] = { position: latLng2 };

    const newActions = this.updateActionState({
      markers,
      polygons,
      measurementMarkers: updatedMarkers,
    });

    dispatch({
      type: 'MOVE_MEASUREMENT_MARKER',
      payload: {
        measurementMarkers: updatedMarkers,
        actions: newActions,
        acIndex: newActions.length - 1,
      },
    });
  };

  onInsert = (index, newPath, pIndex) => {
    const { dispatch } = this.context;
    const { selectedMarker, polygons, drawingMode, measurementMarkers, markers } =
      this.context.state;

    const modifiedPolygons = polygons.map((outer, polyIdx) =>
      polyIdx === pIndex ? { ...outer, path: [...newPath] } : { ...outer }
    );

    const newSelectedMarker = selectedMarker
      ? { ...selectedMarker, position: modifiedPolygons[pIndex].path[index] }
      : { position: modifiedPolygons[pIndex].path[index] };

    if (drawingMode === 'FIELD_HOLE') {
      modifiedPolygons[0] = {
        ...modifiedPolygons[0],
        fittedToField: false,
      };
    }

    const newActions = this.updateActionState({
      markers,
      polygons: modifiedPolygons,
      measurementMarkers,
    });

    dispatch({
      type: 'INSERT_MARKER',
      payload: {
        polygons: modifiedPolygons,
        actions: newActions,
        acIndex: newActions.length - 1,
        selectedMarker: newSelectedMarker,
      },
    });
  };

  onMove = (index, point, newPath, pIndex) => {
    const { dispatch } = this.context;
    const { polygons, drawingMode, measurementMarkers, markers } = this.context.state;

    const modifiedPolygons = polygons.map((outer, polyIdx) =>
      polyIdx === pIndex ? { ...outer, path: [...newPath] } : { ...outer }
    );

    if (drawingMode === 'FIELD_HOLE') {
      modifiedPolygons[0] = {
        ...modifiedPolygons[0],
        fittedToField: false,
      };
    }

    const newActions = this.updateActionState({
      markers,
      polygons: modifiedPolygons,
      measurementMarkers,
    });
    const newAcIndex = newActions.length - 1;

    const selectedMarker = {
      position: modifiedPolygons[pIndex].path[index],
    };

    dispatch({
      type: 'MOVE_MARKER',
      payload: {
        polygons: modifiedPolygons,
        actions: newActions,
        acIndex: newAcIndex,
        selectedMarker,
      },
    });
  };

  updateActionState = ({ markers, polygons, measurementMarkers }) => {
    const { actions, acIndex } = this.context.state;
    const trimmedActions = acIndex !== -1 ? actions.slice(0, acIndex + 1) : [...actions];
    const newAction = {
      path: [...markers],
      polygons: [...polygons],
      measurementMarkers: [...measurementMarkers],
    };
    return [...trimmedActions, [newAction]];
  };

  setIsPolygonValid = (valid, pindex) => {
    const { dispatch } = this.context;
    const { polygons } = this.context.state;

    polygons[pindex].valid = valid;
    dispatch({
      type: 'SET_POLYGONS',
      payload: { polygons: polygons, polygonVisible: polygons.length > 0 },
    });
  };

  setSelectedMarker = (marker) => {
    const { dispatch } = this.context;
    dispatch({ type: 'SET_SELECTED_MARKER', payload: marker });
  };

  setSelectedMeasurementLine = (line, index) => {
    const { dispatch } = this.context;
    line.index = index;
    dispatch({ type: 'SET_SELECTED_MEASUREMENT_LINE', payload: line });
  };

  // ------------------------Rendering------------------------ //
  render() {
    const {
      drawingMode,
      showEditFieldBoundariesConfirmation,
      showSplitFieldConfirmation,
      showEditMergedFieldConfirmation,
    } = this.context.state;
    const { dispatch } = this.context;
    let confirmationProps = null;

    if (
      drawingMode === DRAWING_MODES.EDIT_FIELD_BOUNDARIES &&
      showEditFieldBoundariesConfirmation
    ) {
      confirmationProps = buildEditFieldConfirmation(
        this.props,
        this.context.state,
        this.stopDrawing,
        dispatch
      );
    } else if (drawingMode === DRAWING_MODES.SPLIT_FIELD_POLYGONS && showSplitFieldConfirmation) {
      confirmationProps = buildSplitFieldConfirmation(
        this.props,
        this.context.state,
        this.stopDrawing,
        dispatch
      );
    } else if (drawingMode === DRAWING_MODES.EDIT_MERGED_FIELD && showEditMergedFieldConfirmation) {
      confirmationProps = buildMergeFieldConfirmation(
        this.props,
        this.context.state,
        this.stopDrawing,
        dispatch
      );
    }

    return (
      <Fragment>
        {
          <DrawnPolygons
            map={this.map}
            polygons={this.context.state.polygons}
            polygonVisible={this.context.state.polygonVisible}
            selectedMarker={this.context.state.selectedMarker}
            onInsert={this.onInsert}
            onMove={this.onMove}
            setIsPolygonValid={this.setIsPolygonValid}
            setSelectedMarker={this.setSelectedMarker}
          />
        }

        {
          <MeasurementLines
            map={this.map}
            measurementMarkers={this.context.state.measurementMarkers}
            updateMeasurementMarkers={this.updateMeasurementMarkers}
            setSelectedMeasurementLine={this.setSelectedMeasurementLine}
            markerListenerInstance={this.markerListener?.instance}
          />
        }

        {
          <PolygonMarkers
            map={this.map}
            markers={this.context.state.markers}
            polygonVisible={this.context.state.polygonVisible}
          />
        }

        {<SelectedMarker marker={this.context.state.selectedMarker} />}
        {<SelectedMeasurementLine line={this.context.state.selectedMeasurementLine} />}

        {this.props.controls && (
          <PolygonToolbar
            markerListener={this.markerListener}
            removeMarkerListener={this.removeMarkerListener}
            markerCompleteCallback={this.markerCompleteCallback}
            drawPolyline={this.drawPolyline}
            polyline={this.polyline}
            resetHandler={this.resetHandler}
            isMagicSelecting={this.props.magicWand.isSelecting}
            LangFile={this.props.LangFile}
            toggleDraw={this.toggleDraw}
            measureHandler={this.measureHandler}
          />
        )}

        {
          <DrawingPopover
            popup={this.props.popup}
            contextState={this.context.state}
            LangFile={this.props.LangFile}
            props={this.props}
            dispatch={this.context.dispatch}
            onSubmit={this.onSubmit}
            stopDrawing={this.stopDrawing}
            handleThresholdChanged={this.handleThresholdChanged}
            shouldSubmitButtonDisable={this.shouldSubmitButtonDisable}
          />
        }

        {confirmationProps && <ConfirmationDialog {...confirmationProps} open />}

        {
          <SplitFieldInputDialog
            onConfirm={() => {
              dispatch({ type: 'SET_DRAWING_MODE', payload: 'SPLIT_FIELD_POLYGONS' });
              dispatch({
                type: 'SET_SHOW_DIALOG',
                payload: { key: 'showSplitFieldInputDialog', value: false },
              });
              this.polyline.setMap(null);
              this.removeMarkerListener();
            }}
          />
        }
      </Fragment>
    );
  }
}

PolygonDrawing.propTypes = {
  isDrawingNote: PropTypes.bool.isRequired,
  isDrawingField: PropTypes.bool.isRequired,
  isPlacingMarker: PropTypes.bool.isRequired,
  isDrawingFieldHole: PropTypes.bool.isRequired,
  isEditingFieldBoundaries: PropTypes.bool.isRequired,
  isSplittingField: PropTypes.bool.isRequired,
  isEditingMergedField: PropTypes.bool.isRequired,
  LangFile: PropTypes.object.isRequired,
  onMarkerPlaced: PropTypes.func,
  controls: PropTypes.bool,
  popup: PropTypes.bool,
  fieldImage: PropTypes.obj || PropTypes.any,
};

PolygonDrawing.defaultProps = {
  isDrawingNote: false,
  isDrawingField: false,
  isPlacingMarker: false,
  isDrawingFieldHole: false,
  isEditingFieldBoundaries: false,
  isSplittingField: false,
  isEditingMergedField: false,
  fieldImage: {},
  onMarkerPlaced: voidFunc,
  setIsDrawingField: voidFunc,
  setIsDrawingNote: voidFunc,
  setIsDrawingFieldHole: voidFunc,
  setIsEditingFieldBoundaries: voidFunc,
  setIsSplittingField: voidFunc,
  setIsEditingMergedField: voidFunc,
  setIsMagicSelecting: voidFunc,
  setIsPlacingMarker: voidFunc,
};
